import React from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ProposedBlueprintCard from '../blocks/ProposedBlueprintCard'

const MONO = "'SF Mono', 'Fira Code', 'Cascadia Code', 'JetBrains Mono', Menlo, Consolas, monospace"

function formatTime (iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

const useStyles = makeStyles(() => ({
  messageHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    paddingBottom: '5px',
    marginBottom: '8px',
    borderBottom: '1px solid var(--color-ai-200, rgba(169,88,255,0.18))'
  },
  senderLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    fontFamily: MONO
  },
  userLabel: { color: 'var(--color-neutral-700)' },
  agentLabel: { color: 'var(--color-ai-600)' },
  timestamp: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    fontWeight: 500,
    fontFamily: MONO,
    letterSpacing: '0.2px'
  },
  userHeaderRow: { justifyContent: 'flex-start' },
  agentHeaderRow: { justifyContent: 'flex-end' },
  humanMessage: {
    backgroundColor: 'rgba(255, 255, 255, 0.88)',
    padding: '10px 14px',
    fontSize: '12.5px',
    lineHeight: 1.55,
    wordBreak: 'break-word',
    borderRadius: '4px'
  },
  assistantMessage: {
    fontSize: '12.5px',
    lineHeight: 1.55,
    wordBreak: 'break-word',
    '& pre': {
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
      padding: '8px 12px',
      borderRadius: '4px',
      border: '1px solid var(--color-ai-200, rgba(169,88,255,0.15))',
      overflow: 'auto',
      fontSize: '11.5px',
      fontFamily: MONO
    },
    '& code': {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      padding: '1px 5px',
      borderRadius: '3px',
      fontSize: '11.5px',
      fontFamily: MONO,
      border: '1px solid var(--color-ai-200, rgba(169,88,255,0.12))'
    },
    '& pre code': { backgroundColor: 'transparent', padding: 0, border: 'none' },
    '& p': { margin: '4px 0' },
    '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' },
    '& li': { marginBottom: '2px' },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '11.5px',
      margin: '8px 0',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      borderRadius: '4px'
    },
    '& th, & td': {
      padding: '5px 8px',
      border: '1px solid var(--color-ai-200, rgba(169,88,255,0.15))',
      textAlign: 'left'
    },
    '& th': {
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      fontWeight: 600,
      fontSize: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    }
  },
  toolGutter: {
    margin: '6px 0',
    padding: '6px 10px',
    borderLeft: '2px solid var(--color-ai-300, rgba(169,88,255,0.25))',
    backgroundColor: 'rgba(255, 255, 255, 0.45)',
    borderRadius: '0 4px 4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  toolChipRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1 },
  toolChip: {
    fontSize: '10.5px',
    fontFamily: MONO,
    padding: '3px 8px',
    borderRadius: '3px',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    border: '1px solid var(--color-ai-200, rgba(169,88,255,0.18))',
    color: 'var(--color-ai-700)'
  },
  toolGutterLabel: {
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-ai-400)',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontFamily: MONO
  }
}))

function ChatMessage ({ message, onApplyProposal, autoAccept, appliedProposalIds }) {
  const classes = useStyles()
  const role = message.role

  if (role === 'compaction') {
    const meta = message.metadata || {}
    const n = meta.messagesSummarized
    const label = meta.kind === 'trim'
      ? (n ? `Earlier ${n} messages dropped to stay under the model's input limit` : 'Earlier messages dropped to stay under the model\'s input limit')
      : (n ? `Earlier context compacted — ${n} messages summarized` : 'Earlier context compacted')
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', color: 'var(--color-neutral-500)' }}>
        <span style={{ flex: 1, height: '1px', background: 'var(--color-neutral-200)' }} />
        <span style={{ fontSize: '10.5px', fontFamily: MONO, letterSpacing: '0.3px' }}>{label}</span>
        <span style={{ flex: 1, height: '1px', background: 'var(--color-neutral-200)' }} />
      </div>
    )
  }

  if (message.name === 'system') {
    return (
      <div style={{ textAlign: 'center', fontSize: '11px', opacity: 0.5, padding: '4px 0' }}>
        {typeof message.content === 'string' ? message.content : ''}
      </div>
    )
  }

  if (role === 'user') {
    const content = typeof message.content === 'string'
      ? message.content
      : message.content?.[0]?.text || ''

    return (
      <div>
        <div className={`${classes.messageHeader} ${classes.userHeaderRow}`}>
          <span className={`${classes.senderLabel} ${classes.userLabel}`}>You</span>
          <span className={classes.timestamp}>{formatTime(message.createdAt)}</span>
        </div>
        <div className={classes.humanMessage}>{content}</div>
      </div>
    )
  }

  if (role === 'assistant') {
    const content = typeof message.content === 'string'
      ? message.content
      : Array.isArray(message.content)
        ? message.content.filter(c => c.type === 'text').map(c => c.text).join('')
        : ''

    const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls : []
    const proposals = toolCalls.filter(c => c.name === 'propose_blueprint_change' && c.args?.change)
    const otherTools = toolCalls.filter(c => c.name !== 'propose_blueprint_change')

    const hasContent = !!content
    const hasProposals = proposals.length > 0
    const hasTools = otherTools.length > 0

    if (!hasContent && !hasProposals && !hasTools) return null

    return (
      <div>
        <div className={`${classes.messageHeader} ${classes.agentHeaderRow}`}>
          <span className={classes.timestamp}>{formatTime(message.createdAt)}</span>
          <span className={`${classes.senderLabel} ${classes.agentLabel}`}>Designer</span>
        </div>
        {hasContent && (
          <div className={classes.assistantMessage}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
        {hasTools && (
          <div className={classes.toolGutter}>
            <div className={classes.toolChipRow}>
              {otherTools.map((call, idx) => (
                <span
                  key={call.id || `tool-${idx}`}
                  className={classes.toolChip}
                  title={JSON.stringify(call.args || {})}
                >
                  {call.name}
                </span>
              ))}
            </div>
            <span className={classes.toolGutterLabel}>tools</span>
          </div>
        )}
        {proposals.map((call, idx) => (
          <ProposedBlueprintCard
            key={call.id || `proposal-${idx}`}
            proposalId={call.id}
            change={call.args.change}
            rationale={call.args.rationale}
            testPlan={call.args.testPlan}
            onApply={onApplyProposal}
            autoAccept={autoAccept}
            isAlreadyApplied={appliedProposalIds?.has(call.id) || false}
          />
        ))}
      </div>
    )
  }

  return null
}

ChatMessage.propTypes = {
  message: PropTypes.object.isRequired,
  onApplyProposal: PropTypes.func,
  autoAccept: PropTypes.bool,
  appliedProposalIds: PropTypes.instanceOf(Set)
}

export default ChatMessage
